/**
 * The markdown grammar, in TypeScript (C12).
 *
 * **This is the second implementation and it is deliberate.** `main/richtext.py`
 * is the authority -- it is what the generated .docx prints and what a report
 * exports -- but a form has to render as it is typed, and a round trip to the
 * server per keystroke would make the field feel broken. The same arrangement,
 * for the same reason, as `main/form_rules.py` beside `formdesign/rules.ts`.
 *
 * The two are held together by fixtures: `MARKDOWN_FIXTURES` below is the list
 * asserted in `main/richtext.MARKDOWN_FIXTURES`, so a change to the grammar in
 * one language fails the other language's test rather than producing a form
 * that previews differently from the permit it prints.
 *
 * **Nothing here ever produces HTML.** `MarkdownView` builds React elements
 * from these blocks, so there is no markup to inject, no `dangerouslySetInnerHTML`
 * and no sanitiser -- which is most of why the stored format is markdown and
 * not HTML. See the Python module's docstring for the rest of the argument.
 */

export type Span = {
  text: string;
  bold: boolean;
  italic: boolean;
  code: boolean;
  href: string;
};

export type BlockKind = "heading" | "paragraph" | "bullet" | "number";

export type Block = {
  kind: BlockKind;
  /** 1-3 for a heading, 0 otherwise. */
  level: number;
  spans: Span[];
};

const HEADING_RE = /^(#{1,3})\s+(.*)$/;
const BULLET_RE = /^\s*[-*]\s+(.*)$/;
const NUMBER_RE = /^\s*\d+[.)]\s+(.*)$/;

const span = (text: string, extra: Partial<Span> = {}): Span => ({
  text,
  bold: false,
  italic: false,
  code: false,
  href: "",
  ...extra,
});

const WORD = /[A-Za-z0-9]/;
const isWord = (ch: string | undefined) => !!ch && WORD.test(ch);
const isSpace = (ch: string | undefined) => !ch || /\s/.test(ch);

/**
 * One line to spans.
 *
 * **A hand-written scanner rather than one regular expression**, and that is
 * not a style preference. The Python side expresses the two boundary rules as
 * lookbehind (`(?<=\S)`, `(?<![\w*])`); JavaScript only gained lookbehind in
 * Safari 16.4, and an unsupported one is a **SyntaxError at parse time** -- not
 * a mis-rendered field but a chunk that never loads, which in the field PWA is
 * an app that will not start on an older iPad. The rules are therefore checked
 * as characters, which every engine can do.
 *
 * The rules themselves, both bugs somebody would otherwise report from a real
 * permit: a marker must hug its text (`a * b * c` is arithmetic, not italics)
 * and must not fire mid-word (`2*3*4` is not `234`, `file_name_here` keeps its
 * underscores).
 *
 * One pass, no nesting -- `**bold *and* italic**` yields bold text containing
 * literal asterisks, deliberately: a nested inline grammar needs a real parser
 * in two languages, and a form field wanting one is a field wanting a word
 * processor.
 */
export const parseInline = (line: string): Span[] => {
  const text = line ?? "";
  const spans: Span[] = [];
  let literal = "";
  let i = 0;

  const flush = () => {
    if (literal) spans.push(span(literal));
    literal = "";
  };

  /** The body of a marked run starting at `from`, or null if it is not one. */
  const closing = (marker: string, from: number, forbidInside: boolean) => {
    const start = from + marker.length;
    if (isSpace(text[start])) return null; // must hug its text
    let at = text.indexOf(marker, start);
    while (at > start) {
      const body = text.slice(start, at);
      if (forbidInside && body.includes(marker[0])) return null;
      if (!isSpace(body[body.length - 1])) return { body, end: at + marker.length };
      at = text.indexOf(marker, at + marker.length);
    }
    return null;
  };

  while (i < text.length) {
    const ch = text[i];
    const prev = text[i - 1];

    if (ch === "*" && text[i + 1] === "*") {
      const run = closing("**", i, false);
      if (run) {
        flush();
        spans.push(span(run.body, { bold: true }));
        i = run.end;
        continue;
      }
    }
    if ((ch === "*" || ch === "_") && !isWord(prev) && prev !== ch) {
      const run = closing(ch, i, true);
      // The character after the closing marker decides too: without it,
      // `file_name_here` would italicise its middle.
      if (run && !isWord(text[run.end]) && text[run.end] !== ch) {
        flush();
        spans.push(span(run.body, { italic: true }));
        i = run.end;
        continue;
      }
    }
    if (ch === "`") {
      const at = text.indexOf("`", i + 1);
      if (at > i + 1) {
        flush();
        spans.push(span(text.slice(i + 1, at), { code: true }));
        i = at + 1;
        continue;
      }
    }
    if (ch === "[") {
      const close = text.indexOf("]", i + 1);
      if (close > i + 1 && text[close + 1] === "(") {
        const end = text.indexOf(")", close + 2);
        const href = end > close + 2 ? text.slice(close + 2, end) : "";
        if (href && !/\s/.test(href)) {
          flush();
          spans.push(span(text.slice(i + 1, close), { href }));
          i = end + 1;
          continue;
        }
      }
    }
    literal += ch;
    i += 1;
  }
  flush();
  return spans;
};

/**
 * Markdown to blocks. Total by construction: every line becomes a block, and
 * anything the grammar does not recognise becomes literal text. A form answer
 * must never fail to render because somebody typed an asterisk.
 */
export const parse = (source: string | null | undefined): Block[] =>
  String(source ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((raw) => {
      const line = raw.replace(/\s+$/, "");
      if (!line.trim()) return { kind: "paragraph" as const, level: 0, spans: [] };
      const heading = HEADING_RE.exec(line);
      if (heading)
        return {
          kind: "heading" as const,
          level: heading[1].length,
          spans: parseInline(heading[2]),
        };
      const bullet = BULLET_RE.exec(line);
      if (bullet)
        return { kind: "bullet" as const, level: 0, spans: parseInline(bullet[1]) };
      const numbered = NUMBER_RE.exec(line);
      if (numbered)
        return { kind: "number" as const, level: 0, spans: parseInline(numbered[1]) };
      return { kind: "paragraph" as const, level: 0, spans: parseInline(line) };
    });

/**
 * The answer with its markers removed. The mirror of `richtext.to_text`, and
 * what anything that cannot format (a table cell, a tooltip) shows.
 */
export const toText = (source: string | null | undefined): string => {
  const lines = parse(source).map((block) => {
    const body = block.spans
      .map((s) => (s.href ? `${s.text} (${s.href})` : s.text))
      .join("");
    if (block.kind === "bullet") return `• ${body}`;
    if (block.kind === "number") return `- ${body}`;
    return body;
  });
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  return lines.join("\n");
};

/** One line, for a table cell or a summary row. */
export const summarise = (source: string | null | undefined, limit = 120): string => {
  const flat = toText(source).split(/\s+/).filter(Boolean).join(" ");
  return flat.length <= limit ? flat : `${flat.slice(0, limit - 1).trimEnd()}…`;
};

/** Whether a stored answer carries any formatting at all. */
export const isMarkdown = (value: unknown): boolean => {
  if (typeof value !== "string" || !value) return false;
  return parse(value).some(
    (block) =>
      block.kind !== "paragraph" ||
      block.spans.some((s) => s.bold || s.italic || s.code || s.href)
  );
};

/** Beyond this a rich answer is a document somebody should have attached. */
export const MAX_LENGTH = 20000;

/**
 * Markdown and the plain text it must flatten to, asserted in BOTH languages.
 * The same list as `main/richtext.MARKDOWN_FIXTURES`.
 */
export const MARKDOWN_FIXTURES: [string, string][] = [
  ["plain line", "plain line"],
  ["**bold** and *italic*", "bold and italic"],
  ["_italic_ and `code`", "italic and code"],
  ["# Heading", "Heading"],
  ["- one\n- two", "• one\n• two"],
  ["1. first\n2. second", "- first\n- second"],
  ["see [the standard](https://example.com)", "see the standard (https://example.com)"],
  ["a * b * c", "a * b * c"],
  ["2*3*4", "2*3*4"],
  ["trailing\n\n", "trailing"],
];
